'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useURLParams } from '@/lib/navigation/useURLParams'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { InfoCard } from '@/ui/cards/info-card/InfoCard'
import { UICheckbox } from '@/ui/form/inputs/checkbox/CheckboxInput.client'
import { SearchInput } from '@/ui/form/inputs/search/SearchInput.client'
import { FilterGroup } from '@/ui/form/inputs/filter-group/FilterGroup.client'
import { HighlightedText } from '@/ui/text/highlighted-text/HighlightedText.client'
import { gql } from '@gql-generated/gql'
import type { ProductListFilterValueFilterFragment } from '@gql-generated/graphql'
import { useAtom, useSetAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { isProductListPageLoadingAtom } from '../../services/productList.store'
import { createProductsFiltersDrawerSelectFiltersIntent } from '../../services/productListFilters.intents'
import { currentEmbedFiltersAtom } from '../product-list/services/productList.store'

const PRODUCT_LIST_FILTER_LABEL = 'productListFilter:label'

gql(/* GraphQL */ `
  fragment ProductListFilterValueFilter on ValueFilter {
    title
    productCount
    isActive
    link {
      type
      value
    }
  }
`)

type ProductListFilterProps = {
  title: string

  filters: ProductListFilterValueFilterFragment[]

  /** The number of items to show in the collapsed view */
  collapsedViewItemCount: number

  /** Whether the search input is shown */
  isSearchable?: boolean

  /** The placeholder for the search input */
  searchInputPlaceholder?: string

  /** Overrides the accordion title displayed in the filter group */
  accordionTitle?: string

  isMobile?: boolean
}

export function ProductListFilter({
  title,
  filters,
  collapsedViewItemCount,
  isSearchable,
  searchInputPlaceholder,
  accordionTitle: accordionTitleProp,
  isMobile = false,
}: ProductListFilterProps) {
  const { t } = useTranslation(['productListFilter', 'productListFiltersProduct'])
  const accordionTitle =
    accordionTitleProp ?? title ?? t('productListFiltersProduct:sections.brand.title')

  const setEmbedFilters = useSetAtom(currentEmbedFiltersAtom)
  const { setURLParams } = useURLParams()
  const [showMore, setShowMore] = useState(false)
  const [search, setSearch] = useState('')

  const [filtersState, setFiltersState] = useState(filters)

  const [isProductListPageLoading, setIsProductListPageLoading] = useAtom(
    isProductListPageLoadingAtom,
  )

  const filteredFilters = filtersState.filter((filter) =>
    filter.title.toLowerCase().includes(search.toLowerCase()),
  )
  const slicedFilters = showMore
    ? filteredFilters
    : filteredFilters.slice(0, collapsedViewItemCount)

  const handleChange = (request: string) => {
    const updatedFilters = filtersState.map((filter) => {
      if (filter.link.value === request) {
        return { ...filter, isActive: !filter.isActive }
      }

      return filter
    })

    setFiltersState(updatedFilters)

    handleIntent(createProductsFiltersDrawerSelectFiltersIntent(request), {
      whenInWebView: () => {
        setEmbedFilters(request)
      },
      whenInBrowser: () => {
        setURLParams([
          { name: 'filter', value: request },
          { name: 'p', value: '1' },
        ])
        setIsProductListPageLoading(true)
      },
    })
  }

  useEffect(() => {
    setFiltersState(filters)
  }, [filters])

  return (
    <div className="flex flex-col justify-start gap-y-1">
      {isSearchable && (
        <SearchInput
          aria-label={searchInputPlaceholder}
          data-testid="product-list-filters/search-input"
          name="product-filter-search"
          onChange={(e) => {
            setSearch(e.target.value)
          }}
          onResetAction={() => setSearch('')}
          placeholder={searchInputPlaceholder}
          value={search}
        />
      )}
      {isMobile ? (
        <FilterGroup
          key={accordionTitle}
          multiple
          hideTitle
          className="flex-row flex-wrap gap-1"
          filter={{
            label: accordionTitle,
            values: slicedFilters.map((filter) => ({
              label: t(PRODUCT_LIST_FILTER_LABEL, {
                title: filter.title,
                count: filter.productCount,
              }),
              value: filter.link.value,
            })),
          }}
          selectedValues={slicedFilters
            .filter((filter) => filter.isActive)
            .map((filter) => filter.link.value)}
          onInputChange={(_, filterValue) => {
            const clickedFilter = slicedFilters.find((f) => f.link.value === filterValue)

            if (clickedFilter) {
              const willActivate = !clickedFilter.isActive
              const productCount = clickedFilter.productCount ?? 0
              pushToGtmDataLayer({
                event: 'gaEvent',
                eventCategory: 'filtres',
                eventAction: willActivate
                  ? `application filtre - ${accordionTitle} : ${clickedFilter.title}`
                  : `suppression filtre - ${accordionTitle} : ${clickedFilter.title}`,
                eventLabel: String(productCount),
              })
            }

            handleChange(filterValue)
          }}
        />
      ) : (
        slicedFilters.map((filter) => (
          <UICheckbox
            key={`${filter.title}-${filter.link.value}`}
            value={filter.isActive}
            name={filter.title}
            label={
              <HighlightedText
                highlight={search}
                caseSensitive={false}
                accentSensitive
                color="primary"
              >
                {t(PRODUCT_LIST_FILTER_LABEL, {
                  title: filter.title,
                  count: filter.productCount,
                })}
              </HighlightedText>
            }
            isLoading={false}
            isSubmitting={false}
            isDisabled={isProductListPageLoading}
            showHoverUnderline
            onChange={() => {
              const willActivate = !filter.isActive
              const productCount = filter.productCount ?? 0
              pushToGtmDataLayer({
                event: 'gaEvent',
                eventCategory: 'filtres',
                eventAction: willActivate
                  ? `application filtre - ${accordionTitle} : ${filter.title}`
                  : `suppression filtre - ${accordionTitle} : ${filter.title}`,
                eventLabel: String(productCount),
              })

              handleChange(filter.link.value)
            }}
          />
        ))
      )}
      {filteredFilters.length > collapsedViewItemCount && (
        // TODO: Update when Button V2 is ready
        <button
          data-testid="product-list-filter/show-more"
          className="w-fit !bg-white text-left text-brand-2-900 underline"
          onClick={() => setShowMore(!showMore)}
        >
          {showMore ? t('productListFilter:showLess') : t('productListFilter:showMore')}
        </button>
      )}
      {filteredFilters.length === 0 && (
        <InfoCard
          className="mb-1"
          variant="error"
          title={t('productListFilter:noResults.title')}
          content={t('productListFilter:noResults.content')}
        />
      )}
    </div>
  )
}
